import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeatureFlag } from "featureFlags/constants";
import { useAllFlags } from "featureFlags/hooks/useAllFlags";
import { Role } from "services/rbac/types";
import { useRbacRoles } from "services/rbac/useRbacRoles";

import { SkeletonWizard } from "components/Wizard/SkeletonWizard";

import { EditRoleModal } from "./EditRoleModal/EditRoleModal";
import { EditModal } from "./Modals/EditModal";
import { useEdit } from "./useEdit";

export const useEditActions = () => {
  const [editRole, setEditRole] = useState<Role | null>(null);

  const flags = useAllFlags();
  const rbacRoles = useRbacRoles();
  const { t } = useTranslation();

  const closeEdit = () => {
    setEditRole(null);
  };

  const isRolesWizardEnabled = flags[FeatureFlag.FireboltAppEnableRoleWizard];
  const { isLoading, confirmEdit } = useEdit({ close: closeEdit });

  const handleEdit = (role: Role) => {
    setEditRole(role);
  };

  const onClose = () => {
    if (isLoading) {
      return;
    }
    setEditRole(null);
  };

  const getEditRoleMarkup = () => {
    if (editRole) {
      return isRolesWizardEnabled ? (
        <Suspense
          fallback={
            <SkeletonWizard
              maxWidth="lg"
              size="lg"
              title={t("roles_wizard.title_edit")}
            />
          }
        >
          <EditRoleModal
            onClose={onClose}
            role={editRole}
          />
        </Suspense>
      ) : (
        <EditModal
          editRole={editRole}
          onClose={onClose}
          onConfirm={confirmEdit}
          isLoading={isLoading}
          roles={rbacRoles}
        />
      );
    }
    return null;
  };

  const editRoleMarkup = getEditRoleMarkup();

  return {
    editRole,
    editRoleMarkup,
    handleEdit,
    confirmEdit,
    closeEdit,
  };
};
